import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Dashboard from '@/pages/Dashboard';
import Settings from '@/pages/Settings';
import Energy from '@/pages/Energy';
import Thermometers from '@/pages/Thermometers';
import Lights from '@/pages/Lights';
import Heating from '@/pages/Heating';
import Vehicles from '@/pages/Vehicles';
import Cameras from '@/pages/Cameras';
import Scenarios from '@/pages/Scenarios';
import Admin from '@/pages/Admin';
import Security from '@/pages/Security';
import Budget from '@/pages/Budget';
import Pool from '@/pages/Pool';
import Gates from '@/pages/Gates';
import MobileHome from '@/pages/MobileHome';
import Appliances from '@/pages/Appliances';
import HalLite from '@/pages/HalLite';
import TuyaLocalKeys from '@/pages/TuyaLocalKeys';
import BootSequence from '@/components/BootSequence';
import PinPad from '@/components/PinPad';
import { AuthProvider, useAuth, AUTH_LEVELS } from '@/components/AuthContext';
import { useRandomAlerts, useRealAlerts, AlertPopup, useMicroReboot, MicroRebootOverlay } from '@/components/RandomAlerts';
import { useHALTakeover, HALTakeoverScreen } from '@/components/HALTakeover';
import { ChildLockProvider, setLockedActionCallback } from '@/components/ChildLock';
import { CrazyModeProvider } from '@/components/CrazyMode';
import { HALVideoProvider, useHALVideo } from '@/components/HALVideo';
import HALEyeFullscreen from '@/components/HALEyeFullscreen';
import NotificationOverlay from '@/components/NotificationOverlay';
import EVChargingOverlay from '@/components/EVChargingOverlay';

// Détection iPad Mini 2 (ou ancien iPad)
const isLegacyiPad = () => {
  const ua = navigator.userAgent;
  // iPad Mini 2 a iOS max 12.x et écran 1024x768
  const isiPad = /iPad/.test(ua);
  const isOldiOS = /OS [5-9]_|OS 1[0-2]_/.test(ua);
  const isSmallScreen = window.innerWidth <= 1024 && window.innerHeight <= 768;
  // Aussi détecter via paramètre URL ?lite=1
  const urlParams = new URLSearchParams(window.location.search);
  const forceLite = urlParams.get('lite') === '1';
  
  return forceLite || (isiPad && (isOldiOS || isSmallScreen));
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// API Helper
export const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Détection mobile
const isMobileDevice = () => {
  return window.innerWidth < 768 || 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Composant pour protéger les routes selon le niveau d'accès
const ProtectedRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, hasPermission, isGuest } = useAuth();
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  // Vérifier si l'invité peut accéder à cette page
  if (isGuest() && requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Composant pour afficher un message d'accès restreint
const RestrictedAccess = ({ pageName }) => {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-center p-8 rounded-xl border-2 border-red-500 bg-red-900/20">
        <div className="text-red-400 text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-red-400 mb-2">ACCÈS RESTREINT</h2>
        <p className="text-gray-400 font-mono">
          La page "{pageName}" nécessite un accès administrateur.
        </p>
        <p className="text-gray-500 text-sm mt-4 font-mono">
          Mode invité: Lumières, Chauffe-eau, Thermostat uniquement
        </p>
      </div>
    </div>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState(false);
  const [showPinPad, setShowPinPad] = useState(true);
  const [showReconnecting, setShowReconnecting] = useState(false);
  const [showBoot, setShowBoot] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [forceMobileMode, setForceMobileMode] = useState(null);
  const { alert, dismissAlert } = useRandomAlerts();
  const { realAlert, createRealAlert, dismissRealAlert } = useRealAlerts();
  const { isTakingOver, countdown, triggerTakeover } = useHALTakeover();
  const { isRebooting, rebootLines, rebootReason, triggerMicroReboot } = useMicroReboot();

  // Vérifier si l'utilisateur était déjà authentifié (reconnexion après reboot)
  useEffect(() => {
    const stored = localStorage.getItem('hal3000_auth');
    if (stored) {
      try {
        const { level, timestamp } = JSON.parse(stored);
        const SESSION_DURATION = 24 * 60 * 60 * 1000;
        if (Date.now() - timestamp < SESSION_DURATION && level !== 'none') {
          // Session valide - afficher écran de reconnexion
          setShowReconnecting(true);
          setShowPinPad(false);
          // Reconnexion automatique après 2 secondes
          setTimeout(() => {
            setShowReconnecting(false);
            const showMobile = forceMobileMode === true || (forceMobileMode === null && isMobile);
            if (!showMobile) {
              setShowBoot(true);
            }
          }, 2000);
        }
      } catch (e) {
        // Session invalide, afficher PinPad
      }
    }
  }, [forceMobileMode, isMobile]);

  // Exposer triggerMicroReboot globalement
  useEffect(() => {
    window.triggerMicroReboot = triggerMicroReboot;
    return () => { delete window.triggerMicroReboot; };
  }, [triggerMicroReboot]);

  useEffect(() => {
    const checkApi = async () => {
      try {
        const response = await api.get('/');
        if (response.data) {
          setApiStatus(true);
        }
      } catch (error) {
        console.error('API not available:', error);
      } finally {
        setLoading(false);
      }
    };
    checkApi();
    
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('hal_force_mode');
    if (saved !== null) {
      setForceMobileMode(saved === 'mobile' ? true : saved === 'desktop' ? false : null);
    }
  }, []);

  const handleAuthenticated = (level) => {
    setShowPinPad(false);
    // Afficher le boot sequence après authentification (desktop uniquement)
    const showMobile = forceMobileMode === true || (forceMobileMode === null && isMobile);
    if (!showMobile) {
      setShowBoot(true);
    }
  };

  const handleBootComplete = () => {
    setShowBoot(false);
  };

  const switchToDesktop = () => {
    setForceMobileMode(false);
    localStorage.setItem('hal_force_mode', 'desktop');
  };

  const switchToMobile = () => {
    setForceMobileMode(true);
    localStorage.setItem('hal_force_mode', 'mobile');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-cyan-400 text-xl font-mono">INITIALIZING...</div>
      </div>
    );
  }

  // Rediriger iPad Mini 2 / anciens appareils vers HAL Lite
  const isLegacy = isLegacyiPad();
  const isOnLitePage = window.location.pathname === '/lite';
  
  if (isLegacy && !isOnLitePage) {
    window.location.href = '/lite';
    return null;
  }

  // HAL Lite - pas d'authentification requise
  if (isOnLitePage) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/lite" element={<HalLite />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  const showMobile = forceMobileMode === true || (forceMobileMode === null && isMobile);

  // Écran de reconnexion (après reboot avec session valide)
  if (showReconnecting) {
    return (
      <AuthProvider>
        <div className="fixed inset-0 bg-black flex items-center justify-center">
          <div className="text-center">
            {/* HAL Eye */}
            <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-red-600 via-red-500 to-orange-500 shadow-[0_0_60px_rgba(255,0,0,0.8)] animate-pulse flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-900 to-black" />
            </div>
            
            {/* Message */}
            <div className="text-green-400 font-mono text-xl mb-4 animate-pulse">
              UTILISATEUR AUTHENTIFIÉ
            </div>
            <div className="text-cyan-400 font-mono text-lg">
              Reconnexion en cours...
            </div>
            
            {/* Loading bar */}
            <div className="mt-8 w-64 h-2 bg-gray-800 rounded-full mx-auto overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-500 to-cyan-500 animate-[loading_2s_ease-in-out]" 
                   style={{ animation: 'loading 2s ease-in-out forwards' }} />
            </div>
            
            <style>{`
              @keyframes loading {
                0% { width: 0%; }
                100% { width: 100%; }
              }
            `}</style>
          </div>
        </div>
      </AuthProvider>
    );
  }

  // Afficher le PinPad en premier
  if (showPinPad) {
    return (
      <AuthProvider>
        <PinPad onAuthenticated={handleAuthenticated} />
      </AuthProvider>
    );
  }

  // Version Mobile - pas de boot sequence
  if (showMobile) {
    return (
      <AuthProvider>
        <MobileHome onSwitchToDesktop={switchToDesktop} />
      </AuthProvider>
    );
  }

  // Boot sequence (desktop only, après authentification)
  if (showBoot) {
    return (
      <AuthProvider>
        <BootSequence onComplete={handleBootComplete} />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <HALVideoProvider>
        <AppContent 
          alert={alert}
          dismissAlert={dismissAlert}
          realAlert={realAlert}
          dismissRealAlert={dismissRealAlert}
          isTakingOver={isTakingOver}
          countdown={countdown}
        />
        <HALEyeFullscreen />
        <MicroRebootOverlay 
          isRebooting={isRebooting} 
          rebootLines={rebootLines} 
          rebootReason={rebootReason} 
        />
      </HALVideoProvider>
    </AuthProvider>
  );
}

// Composant interne pour accéder au contexte HALVideo
function AppContent({ alert, dismissAlert, realAlert, dismissRealAlert, isTakingOver, countdown }) {
  const { handleScreenTap, triggerFromLockedAction } = useHALVideo();
  const { isGuest, hasPermission } = useAuth();

  useEffect(() => {
    setLockedActionCallback(triggerFromLockedAction);
  }, [triggerFromLockedAction]);

  return (
    <ChildLockProvider>
      <CrazyModeProvider>
        <BrowserRouter>
        <div onClick={handleScreenTap} className="contents">
          <HALTakeoverScreen 
            isActive={isTakingOver} 
            countdown={countdown}
            onOverride={() => window.location.reload()} 
          />
          
          <AlertPopup alert={alert} onDismiss={dismissAlert} isRealAlert={false} />
          <AlertPopup alert={realAlert} onDismiss={dismissRealAlert} isRealAlert={true} />
          <NotificationOverlay />
          <EVChargingOverlay />
          
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/settings" element={
              hasPermission('settings') ? <Settings /> : <RestrictedAccess pageName="Configuration" />
            } />
            <Route path="/energy" element={
              hasPermission('energy') ? <Energy /> : <RestrictedAccess pageName="Énergie" />
            } />
            <Route path="/thermometers" element={<Thermometers />} />
            <Route path="/lights" element={<Lights />} />
            <Route path="/heating" element={<Heating />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/cameras" element={
              hasPermission('security') ? <Cameras /> : <RestrictedAccess pageName="Caméras" />
            } />
            <Route path="/scenarios" element={
              hasPermission('scenarios') ? <Scenarios /> : <RestrictedAccess pageName="Scénarios" />
            } />
            <Route path="/admin" element={
              hasPermission('admin') ? <Admin /> : <RestrictedAccess pageName="Administration" />
            } />
            <Route path="/security" element={
              hasPermission('security') ? <Security /> : <RestrictedAccess pageName="Sécurité" />
            } />
            <Route path="/budget" element={
              hasPermission('budget') ? <Budget /> : <RestrictedAccess pageName="Budget" />
            } />
            <Route path="/pool" element={
              hasPermission('pool') ? <Pool /> : <RestrictedAccess pageName="Piscine" />
            } />
            <Route path="/gates" element={
              hasPermission('gates') ? <Gates /> : <RestrictedAccess pageName="Portails" />
            } />
            <Route path="/appliances" element={<Appliances />} />
            <Route path="/settings/tuya-local-keys" element={
              hasPermission('settings') ? <TuyaLocalKeys /> : <RestrictedAccess pageName="Tuya Local Keys" />
            } />
            <Route path="/lite" element={<HalLite />} />
          </Routes>
        </div>
      </BrowserRouter>
      </CrazyModeProvider>
    </ChildLockProvider>
  );
}

export default App;
// Cache buster 1773426536
